// stores/useStockSymbolsStore.ts
import { defineStore } from "pinia";
import axios from "axios";
import { ref, shallowRef, computed, watch, reactive } from "vue";
import localforage from "localforage";

import type { MostUpToDateDataType } from "../utils/types";
import { getSongbirdApiBase, getSongbirdWsBase } from "../utils/songbird-client";
import { fetchNewsFeedTopGainers } from "../utils/news-feed-client";

/**
 * Interface representing a stock item with its properties
 * @interface StockItem
 * @property {string} SYMBOL - The stock symbol/ticker
 * @property {string} NAME - The company name
 * @property {number} PC_CHG - The percentage change in price
 * @property {number} NET_CHG - The net change in price
 * @property {number} BID - The current bid price
 * @property {any} [key: string] - Additional dynamic properties
 */
export interface StockItem {
	SYMBOL: string;
	NAME: string;
	PC_CHG: number;
	NET_CHG: number;
	BID: number;
	[key: string]: any;
}

type ReferencedStocksInfo = {
	[key: string]: MostUpToDateDataType;
};

// Define the localforage instance outside the store definition
// so it's created only once when the module is imported.
const stockCache = localforage.createInstance({
	name: "stockDB",
	storeName: "indexSymbols",
	driver: localforage.INDEXEDDB // Ensure INDEXEDDB is available, or provide fallbacks
});

// Client calls Songbird directly via songbirdProxy (see utils/songbird-client.ts).

// Define the store using the setup function syntax.
// NOTE: a unique Pinia id ("songbirdStocks") is used so this Songbird-backed
// store does not collide with the legacy options store in stores/stocks.ts,
// which also registers under the id "stocks" and is still used by the
// news/blog pages and the default layout.
export const useStocksStore = defineStore("songbirdStocks", () => {
	// State: Define reactive state using ref or reactive
	const symbols_list = shallowRef<any[]>([]);
	const lastUpdated = ref<number | null>(null);
	const isLoading = ref(false);
	const error = ref<Error | null>(null);
	const most_active_stocks = shallowRef<{ [key: string]: StockItem }>({});
	const top_gainers_stocks = shallowRef<StockItem[]>([]);
	const top_gainers_loaded = ref(false);
	const top_losers_stocks = shallowRef<StockItem[]>([]);
	const stocks = shallowRef<ReferencedStocksInfo>();
	const pollingInterval = ref<NodeJS.Timeout | null>(null);
	// Timestamp (ms) until which stock-data polling should be paused after a 429 response
	const rateLimitedUntil = ref<number>(0);

	// ── WebSocket transport ─────────────────────────────────────────────────
	// One shared WS connection for the entire store. All callers (fetchStocks,
	// startTrackingSymbols, stock_subscribed_symbols watcher) funnel their
	// symbols here. REST polling is kept as an automatic fallback.

	const ws = shallowRef<WebSocket | null>(null);
	/** Aggregate of ALL symbols any caller wants live updates for. */
	const wsSymbols = ref(new Set<string>());
	const wsReconnectAttempts = ref(0);
	const wsReconnectTimer = shallowRef<ReturnType<typeof setTimeout> | null>(null);
	const usingWs = ref(false);
	/** Symbols from fetchStocks/startTrackingSymbols — used by the REST fallback. */
	const trackedSymbols = ref<string[]>([]);

	const WS_MAX_RECONNECT_ATTEMPTS = 5;
	const WS_BASE_DELAY_MS = 1000;
	const REST_POLL_INTERVAL_MS = 10_000;

	/** Apply a `quote_update` payload to both store caches. */
	const applyQuoteUpdate = (rows: any[]) => {
		if (!rows || rows.length === 0) return;

		// Update `stocks` (read by fetchStocks/startTrackingSymbols callers)
		const updated: Record<string, MostUpToDateDataType> = { ...(stocks.value || {}) };
		rows.forEach((stock: any) => {
			if (!stock?.SYMBOL) return;
			const existing = updated[stock.SYMBOL];
			updated[stock.SYMBOL] = {
				...(existing || {}),
				...stock,
				YTD_CHG: stock.YTD_CHG ?? existing?.YTD_CHG ?? 0,
				YTD_PCT: stock.YTD_PCT ?? existing?.YTD_PCT ?? 0
			};
		});
		stocks.value = updated;

		// Update `stock_subscriptions` (read by TopicCard / MarketsTrends badges)
		addMultipleStockSubscriptions(rows);
	};

	/** Send an updated `subscribe` message if the socket is open. */
	const resubscribeWs = () => {
		if (ws.value?.readyState === 1 /* OPEN */ && wsSymbols.value.size > 0) {
			ws.value.send(
				JSON.stringify({
					type: "subscribe",
					symbols: Array.from(wsSymbols.value).join(",")
				})
			);
		}
	};

	const startRestFallback = () => {
		if (pollingInterval.value || trackedSymbols.value.length === 0) return;
		pollingInterval.value = setInterval(() => {
			fetchStocksData(trackedSymbols.value, false);
		}, REST_POLL_INTERVAL_MS);
	};

	const stopWs = () => {
		if (wsReconnectTimer.value) {
			clearTimeout(wsReconnectTimer.value);
			wsReconnectTimer.value = null;
		}
		ws.value?.close();
		ws.value = null;
		usingWs.value = false;
		wsReconnectAttempts.value = 0;
	};

	const scheduleWsReconnect = () => {
		if (wsSymbols.value.size === 0) return;

		if (wsReconnectAttempts.value >= WS_MAX_RECONNECT_ATTEMPTS) {
			console.warn(
				"[StocksStore] WS max reconnect attempts reached — falling back to REST polling"
			);
			startRestFallback();
			return;
		}

		const delay = Math.min(
			WS_BASE_DELAY_MS * 2 ** wsReconnectAttempts.value,
			30000
		);

		wsReconnectTimer.value = setTimeout(() => {
			wsReconnectAttempts.value++;
			connectWs();
		}, delay);
	};

	const connectWs = () => {
		if (!import.meta.client || wsSymbols.value.size === 0) return;

		const wsBase = getSongbirdWsBase();
		if (!wsBase || wsBase === "ws://" || wsBase === "wss://") {
			console.warn("[StocksStore] WS base not configured — using REST polling");
			startRestFallback();
			return;
		}

		const symbols = Array.from(wsSymbols.value);
		const wsUrl = `${wsBase}ws/stocks/quote?symbols=${symbols
			.map(encodeURIComponent)
			.join(",")}`;

		try {
			const socket = new WebSocket(wsUrl);

			socket.onopen = () => {
				wsReconnectAttempts.value = 0;
			};

			socket.onmessage = (event) => {
				try {
					const msg = JSON.parse(event.data as string);

					switch (msg.type) {
						case "connected":
							usingWs.value = true;
							// Stop any running REST interval — WS is serving updates now
							if (pollingInterval.value) {
								clearInterval(pollingInterval.value);
								pollingInterval.value = null;
							}
							break;

						case "quote_update": {
							const rows = Array.isArray(msg.data)
								? msg.data
								: msg.data
									? [msg.data]
									: [];
							applyQuoteUpdate(rows);
							break;
						}

						case "subscribed":
							// Acknowledgement — nothing to do
							break;

						case "status":
							// Outside active trading hours — no data to merge
							break;

						case "error":
							console.error("[StocksStore] WS server error:", msg.error);
							break;
					}
				} catch {
					// Ignore malformed frames
				}
			};

			socket.onclose = () => {
				usingWs.value = false;
				if (ws.value === socket) ws.value = null;
				scheduleWsReconnect();
			};

			socket.onerror = () => {
				// onclose will fire immediately after; reconnect handled there
			};

			ws.value = socket;
		} catch (error) {
			console.error("[StocksStore] Failed to open WS:", error);
			scheduleWsReconnect();
		}
	};
	// ── end WebSocket transport ─────────────────────────────────────────────

	const stock_subscribed_symbols = ref(new Set<string>());
	const stock_subscriptions = reactive(new Map<string, StockItem>());

	// Constants can be defined within the setup scope
	const CACHE_DURATION_MS = 1000 * 60 * 60; // 1 hour

	// Getters: Define computed properties
	const isDataStale = computed(() => {
		// Access state refs using .value
		return (
			lastUpdated.value === null ||
			Date.now() - lastUpdated.value > CACHE_DURATION_MS
		);
	});

	// Actions: Define functions directly inside the setup function
	// Define refresh first as init calls it
	const refresh = async () => {
		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			const symbolsListUrl = `${getSongbirdApiBase()}symbols-list`;
			const res = await axios.get(symbolsListUrl);
			const fetchedData = res.data; // Use a different variable name to avoid confusion

			const lastUpdatedTimestamp = Date.now();
			// Update state refs using .value
			symbols_list.value = fetchedData;
			lastUpdated.value = lastUpdatedTimestamp;

			// Update local cache
			await stockCache.setItem("symbols", fetchedData); // Cache the data from the ref
			await stockCache.setItem("symbols_last_updated", lastUpdatedTimestamp); // Cache the timestamp from the ref
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols refresh:", err.response.data);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	const init = async () => {
		// Prevent multiple initializations if already loading
		if (isLoading.value) {
			// Access state refs with .value
			return;
		}

		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			const cached = await stockCache.getItem<any[]>("symbols");
			const timestamp = await stockCache.getItem<number>("symbols_last_updated");

			// Check if cached data exists AND the timestamp is not null AND the cache is still valid
			if (
				cached &&
				timestamp !== null &&
				Date.now() - timestamp < CACHE_DURATION_MS
			) {
				symbols_list.value = cached; // Update state ref from cache
				lastUpdated.value = timestamp; // Update state ref from cache timestamp
			} else {
				await refresh(); // Call the refresh action (defined above)
			}
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols init:", err);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	function setMostActiveStocks(
		stocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const stocksObject = Array.isArray(stocks)
			? stocks.reduce((acc, stock) => {
					acc[stock.SYMBOL] = stock;
					return acc;
			  }, {} as { [key: string]: StockItem })
			: stocks;

		// Only update if data has changed
		const currentStocks = most_active_stocks.value;
		const hasChanged =
			Object.keys(stocksObject).some((symbol) => {
				const currentStock = currentStocks[symbol];
				const newStock = stocksObject[symbol];

				if (!currentStock) {
					return true; // New stock
				}

				// Compare key properties that indicate real changes
				return (
					currentStock.BID !== newStock.BID ||
					currentStock.PC_CHG !== newStock.PC_CHG ||
					currentStock.NET_CHG !== newStock.NET_CHG ||
					currentStock.NAME !== newStock.NAME
				);
			}) || Object.keys(currentStocks).length !== Object.keys(stocksObject).length;

		if (hasChanged) {
			most_active_stocks.value = stocksObject;
		}
	}

	function updateMostActiveStocksCurrentData(
		currentDataStocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const currentDataObject = Array.isArray(currentDataStocks)
			? currentDataStocks.reduce((acc, stock) => {
					acc[stock.SYMBOL] = stock;
					return acc;
			  }, {} as { [key: string]: StockItem })
			: currentDataStocks;

		// Get existing stocks with historic data
		const existingStocks = most_active_stocks.value;

		// Only update if current data has changed
		const hasChanged = Object.keys(currentDataObject).some((symbol) => {
			const existingStock = existingStocks[symbol];
			const newCurrentData = currentDataObject[symbol];

			if (!existingStock) {
				return true; // New stock
			}

			// Compare only current data properties (not historic data)
			return (
				existingStock.BID !== newCurrentData.BID ||
				existingStock.PC_CHG !== newCurrentData.PC_CHG ||
				existingStock.NET_CHG !== newCurrentData.NET_CHG ||
				existingStock.NAME !== newCurrentData.NAME
			);
		});

		if (hasChanged) {
			// Update stocks by preserving historic data (prices, labels) and updating current data
			const updatedStocks = { ...existingStocks };

			Object.keys(currentDataObject).forEach((symbol) => {
				const existingStock = updatedStocks[symbol];
				const newCurrentData = currentDataObject[symbol];

				// Preserve historic data if it exists, update current data
				updatedStocks[symbol] = {
					...newCurrentData, // New current data
					prices: existingStock?.prices || [], // Preserve existing prices
					labels: existingStock?.labels || [] // Preserve existing labels
				};
			});

			most_active_stocks.value = updatedStocks;
		}
	}

	function setTopGainerStocks(stocks: StockItem[]) {
		top_gainers_stocks.value = [...stocks];
	}

	function setTopLoserStocks(stocks: StockItem[]) {
		top_losers_stocks.value = [...stocks];
	}

	// Guards against concurrent/duplicate requests when multiple
	// TopGainerStocks instances mount (e.g. mobile + desktop variants).
	const top_gainers_loading = ref(false);
	const top_gainers_history_loading = ref(false);

	const topGainersNeedHistory = (stocks: StockItem[]) =>
		stocks.some((s) => !s.prices || s.prices.length === 0);

	/**
	 * Build a sorted StockItem[] out of the bundled news-feed/top-gainers
	 * response: { quotes: { SYMBOL: [stockObject] }, sparklines: { SYMBOL: [{time,date,close}] } }.
	 */
	const buildStocksArrayFromTopGainersBundle = (
		bundle: { quotes: Record<string, any>; sparklines: Record<string, any[]> },
		limit: number
	): StockItem[] => {
		return Object.values(bundle.quotes)
			.map((value: any) => (Array.isArray(value) ? value[0] : value))
			.filter((item: any): item is StockItem => Boolean(item && item.SYMBOL))
			.map((item: any) => {
				const history = bundle.sparklines?.[item.SYMBOL] || [];
				return {
					...item,
					prices: history
						.map((d: any) => d.close)
						.filter((v: any) => typeof v === "number"),
					labels: history.map((d: any) => d.time ?? d.date)
				};
			})
			.sort((a, b) => (b.PC_CHG ?? 0) - (a.PC_CHG ?? 0))
			.slice(0, limit);
	};

	/**
	 * Seed top_gainers_stocks directly from an already-fetched
	 * news-feed/top-gainers bundle (e.g. fetched once by a page-level
	 * `useAsyncData` call). Lets pages prime the store ahead of
	 * TopGainerStocks/QuoteFinder mounting, so those components skip their
	 * own client-side fetch entirely (guarded by the
	 * `top_gainers_stocks.length > 0` check in `fetchTopGainerStocks`).
	 * Returns true if the bundle produced usable data.
	 */
	const primeTopGainerStocksFromBundle = (
		bundle: { quotes: Record<string, any>; sparklines: Record<string, any[]> } | null | undefined,
		limit: number = 20
	): boolean => {
		if (!bundle || top_gainers_stocks.value.length > 0) {
			return false;
		}

		const stocksArray = buildStocksArrayFromTopGainersBundle(bundle, limit);
		if (stocksArray.length === 0) {
			return false;
		}

		setTopGainerStocks(stocksArray);
		top_gainers_loaded.value = true;
		return true;
	};

	/**
	 * Fetch the "top gainers" market filter from Songbird and populate
	 * top_gainers_stocks. Prefers the flattened, parameter-free
	 * `news-feed/top-gainers` bundle (quotes + sparklines in one request);
	 * falls back to the original two-request flow (`stocks/filtered` then
	 * `stocks/history`) if the bundle is unavailable or incomplete.
	 */
	const fetchTopGainerStocks = async (limit: number = 20) => {
		if (import.meta.server) {
			return;
		}

		if (top_gainers_loading.value) {
			return;
		}

		// Skip refetching the list when we already have it, but still backfill
		// sparkline history if a prior request failed or was incomplete.
		if (top_gainers_stocks.value.length > 0) {
			if (topGainersNeedHistory(top_gainers_stocks.value)) {
				void fetchTopGainersHistory();
			}
			return;
		}

		if (Date.now() < rateLimitedUntil.value) {
			return;
		}

		top_gainers_loading.value = true;

		try {
			const bundle = await fetchNewsFeedTopGainers().catch((err) => {
				console.warn(
					"[StocksStore][fetchTopGainerStocks] Bundled news-feed/top-gainers fetch failed, falling back:",
					(err as any)?.message || err
				);
				return null;
			});

			if (bundle) {
				const stocksArray = buildStocksArrayFromTopGainersBundle(bundle, limit);
				if (stocksArray.length > 0) {
					setTopGainerStocks(stocksArray);
					// Backfill sparklines in the background if the bundle's
					// history came back incomplete for any symbol.
					if (topGainersNeedHistory(stocksArray)) {
						void fetchTopGainersHistory();
					}
					return;
				}
			}

			// Fallback: original two-request flow (kept as a resilience feature).
			const res = await axios.get(`${getSongbirdApiBase()}stocks/filtered`, {
				params: { value: "top_gainers", is_sector: false, limit },
				timeout: 15000,
				validateStatus: () => true
			});

			if (res.status === 429) {
				const retryAfterSeconds = parseInt(
					(res.headers as Record<string, string>)?.["retry-after"] ?? "60",
					10
				);
				rateLimitedUntil.value = Date.now() + retryAfterSeconds * 1000;
				console.warn(
					`[StocksStore][fetchTopGainerStocks] Rate limited by API (429) — pausing for ${retryAfterSeconds}s`
				);
				return;
			}

			if (res.status !== 200 || !res.data || res.data.error) {
				return;
			}

			// Flatten { SYMBOL: [item] } into a sorted StockItem[] and ensure the
			// sparkline-related fields exist so TopGainerBlock can render safely.
			const stocksArray: StockItem[] = Object.values(res.data)
				.map((value: any) => (Array.isArray(value) ? value[0] : value))
				.filter((item: any): item is StockItem => Boolean(item && item.SYMBOL))
				.map((item: any) => ({
					...item,
					prices: item.HISTORIC_DATA?.map((d: any) => d.close) ?? item.prices ?? [],
					labels: item.HISTORIC_DATA?.map((d: any) => d.date) ?? item.labels ?? []
				}))
				.sort((a, b) => (b.PC_CHG ?? 0) - (a.PC_CHG ?? 0));

			setTopGainerStocks(stocksArray);

			// Enrich with sparkline history in the background so the list renders
			// immediately while the charts populate a moment later.
			void fetchTopGainersHistory();
		} catch (error) {
			console.error("[StocksStore][fetchTopGainerStocks] ERROR:", error);
		} finally {
			top_gainers_loaded.value = true;
			top_gainers_loading.value = false;
		}
	};

	/**
	 * Fetch 1-day intraday history for the current top-gainer symbols and merge
	 * the close prices into top_gainers_stocks so TopGainerBlock can render the
	 * sparkline charts. Songbird caps processing at 16 symbols per request.
	 */
	const fetchTopGainersHistory = async () => {
		const current = top_gainers_stocks.value;
		const symbols = current.map((s) => s.SYMBOL).filter(Boolean);

		if (
			symbols.length === 0 ||
			Date.now() < rateLimitedUntil.value ||
			top_gainers_history_loading.value ||
			!topGainersNeedHistory(current)
		) {
			return;
		}

		top_gainers_history_loading.value = true;

		try {
			const res = await axios.get(`${getSongbirdApiBase()}stocks/history`, {
				params: {
					symbols: symbols.join(","),
					range: "1D",
					fields: "time,date,close"
				},
				timeout: 20000,
				validateStatus: () => true
			});

			if (res.status !== 200 || !res.data?.results) {
				return;
			}

			const results = res.data.results as Record<string, any[]>;

			const enriched = top_gainers_stocks.value.map((stock) => {
				const history = results[stock.SYMBOL];
				if (!Array.isArray(history) || history.length === 0) {
					return stock;
				}

				return {
					...stock,
					prices: history
						.map((d: any) => d.close)
						.filter((v: any) => typeof v === "number"),
					labels: history.map((d: any) => d.time ?? d.date)
				};
			});

			setTopGainerStocks(enriched);
		} catch (error) {
			console.error("[StocksStore][fetchTopGainersHistory] ERROR:", error);
		} finally {
			top_gainers_history_loading.value = false;
		}
	};

	const getMostActiveStocks = computed(() => {
		if (
			!most_active_stocks.value ||
			Object.keys(most_active_stocks.value).length === 0
		) {
			return [];
		}
		return Object.values(most_active_stocks.value).map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopGainerStocks = computed(() => {
		if (!top_gainers_stocks.value || top_gainers_stocks.value.length === 0) {
			return [];
		}
		return top_gainers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopLoserStocks = computed(() => {
		if (!top_losers_stocks.value || top_losers_stocks.value.length === 0) {
			return [];
		}
		return top_losers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const fetchStocksData = async (
		symbols: string[],
		includeYtd: boolean = true
	) => {
		if (import.meta.server) {
			return;
		}

		// If the API previously returned 429, honour the Retry-After window before trying again
		if (Date.now() < rateLimitedUntil.value) {
			return;
		}

		let payload: MostUpToDateDataType[] = [];

		try {
			const params: Record<string, string> = {
				symbol: symbols.join(",")
			};

			if (includeYtd) {
				params.include_ytd = "true";
			}

			const res = await axios.get(`${getSongbirdApiBase()}stocks/quote`, {
				params,
				timeout: 15000,
				validateStatus: () => true
			});

			if (res.status === 429) {
				// Back off for the server-specified duration (or 60 s if header is absent)
				const retryAfterSeconds = parseInt(
					(res.headers as Record<string, string>)?.["retry-after"] ?? "60",
					10
				);
				rateLimitedUntil.value = Date.now() + retryAfterSeconds * 1000;
				console.warn(
					`[StocksStore][fetchStocksData] Rate limited by API (429) — pausing stock polling for ${retryAfterSeconds}s`
				);
				return;
			}

			if (res.status === 200) {
				rateLimitedUntil.value = 0; // Clear any prior backoff on success
				payload = res.data?.data || res.data;
			}
		} catch (error) {
			console.error("[StocksStore][fetchStocksData] ERROR:", error);
			return { data: [] };
		}

		const finalPayload = payload.reduce(
			(acc: Record<string, MostUpToDateDataType>, stock: MostUpToDateDataType) => {
				const existing = stocks.value?.[stock.SYMBOL];
				acc[stock.SYMBOL] = {
					...(existing || {}),
					...stock,
					YTD_CHG: stock.YTD_CHG ?? existing?.YTD_CHG ?? 0,
					YTD_PCT: stock.YTD_PCT ?? existing?.YTD_PCT ?? 0
				};
				return acc;
			},
			{} as Record<string, MostUpToDateDataType>
		);

		stocks.value = finalPayload;
	};

	const fetchStocks = async (symbols: string[]) => {
		stopWs();
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		trackedSymbols.value = symbols;
		symbols.forEach((s) => wsSymbols.value.add(s.toUpperCase()));

		// Immediate REST fetch for first paint
		await fetchStocksData(symbols, true);

		// Then switch to WS push — falls back to REST interval automatically
		connectWs();
	};

	const cleanup = () => {
		stopWs();
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}
		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
		wsSymbols.value.clear();
		trackedSymbols.value = [];
	};

	// Symbol tracking methods
	const startTrackingSymbols = async (symbols: string[]) => {
		stopWs();
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		// Filter out invalid symbols (company names with spaces, special chars, etc.)
		const validSymbols = symbols.filter(
			(symbol) =>
				symbol && symbol.trim() !== "" && /^[A-Za-z0-9.\-]+$/.test(symbol)
		);

		if (validSymbols.length === 0) return;

		trackedSymbols.value = validSymbols;
		validSymbols.forEach((s) => wsSymbols.value.add(s.toUpperCase()));

		// Immediate REST fetch for first paint (includes YTD)
		await fetchStocksData(validSymbols, true);

		// Then switch to WS push — falls back to REST interval automatically
		connectWs();
	};

	const stopTrackingSymbols = () => {
		stopWs();
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
		wsSymbols.value.clear();
		trackedSymbols.value = [];
	};

	// YTD data fetching method (single batched request for all symbols)
	const fetchYtdDataForStocks = async () => {
		if (!stocks.value || Object.keys(stocks.value).length === 0) {
			return;
		}

		try {
			const symbols = Object.keys(stocks.value);

			const response = await axios.get(`${getSongbirdApiBase()}stocks/history`, {
				params: {
					symbol: symbols.join(","),
					ytd: "true",
					fields: "YTD_CHG,YTD_PCT"
				},
				timeout: 15000
			});

			if (response.data?.results) {
				const results = response.data.results;
				const updated = { ...stocks.value };

				symbols.forEach((symbol) => {
					const ytdData = results[symbol];
					if (ytdData && updated[symbol]) {
						updated[symbol] = {
							...updated[symbol],
							YTD_CHG: ytdData.YTD_CHG || 0,
							YTD_PCT: ytdData.YTD_PCT || 0
						};
					}
				});

				stocks.value = updated;
			}
		} catch (error) {
			console.error("[StocksStore] Error fetching YTD data:", error);
		}
	};

	// Stock subscriptions management methods
	/**
	 * Add a stock to the subscriptions map
	 * @param stockItem - The stock item to add (SYMBOL will be used as the key)
	 */
	const addStockSubscription = (stockItem: StockItem) => {
		stock_subscriptions.set(stockItem.SYMBOL, stockItem);
	};

	/**
	 * Add multiple stocks to the subscriptions map
	 * @param stockItems - Array of stock items to add
	 */
	const addMultipleStockSubscriptions = (stockItems: StockItem[]) => {
		stockItems.forEach((stockItem) => {
			stock_subscriptions.set(stockItem.SYMBOL, stockItem);
		});
	};

	/**
	 * Update a specific stock in the subscriptions map
	 * @param symbol - The stock symbol to update
	 * @param updates - Partial stock data to update
	 */
	const updateStockSubscription = (
		symbol: string,
		updates: Partial<StockItem>
	) => {
		const existingStock = stock_subscriptions.get(symbol);
		if (existingStock) {
			const updatedStock = { ...existingStock, ...updates };
			stock_subscriptions.set(symbol, updatedStock);
		} else {
			console.warn(
				`[StocksStore] Cannot update ${symbol}: not found in subscriptions`
			);
		}
	};

	/**
	 * Remove a stock subscription
	 * @param symbol - The stock symbol to remove
	 */
	const removeStockSubscription = (symbol: string) => {
		const removed = stock_subscriptions.delete(symbol);
		if (!removed) {
			console.warn(
				`[StocksStore] Cannot remove ${symbol}: not found in subscriptions`
			);
		}
		return removed;
	};

	/**
	 * Clear all stock subscriptions
	 */
	const clearStockSubscriptions = () => {
		stock_subscriptions.clear();
	};

	/**
	 * Check if a stock is subscribed
	 * @param symbol - The stock symbol to check
	 */
	const hasStockSubscription = (symbol: string): boolean => {
		return stock_subscriptions.has(symbol);
	};

	/**
	 * Get a specific stock subscription
	 * @param symbol - The stock symbol to get
	 */
	const getStockSubscription = (symbol: string): StockItem | undefined => {
		return stock_subscriptions.get(symbol);
	};

	/**
	 * Extract symbols from odd indices of stock_pairs array and save to stock_subscribed_symbols set
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const setSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Clear existing symbols
		stock_subscribed_symbols.value.clear();

		// Extract symbols from odd indices and add to set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});
	};

	/**
	 * Add symbols from stock_pairs array to existing stock_subscribed_symbols set (without clearing)
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const addSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Extract symbols from odd indices and add to existing set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});
	};

	// Computed getters for stock subscriptions
	const getStockSubscriptions = computed(() => {
		return Array.from(stock_subscriptions.values());
	});

	const getStockSubscriptionsMap = computed(() => {
		return stock_subscriptions;
	});

	const getStockSubscriptionsCount = computed(() => {
		return stock_subscriptions.size;
	});

	const getSubscribedSymbols = computed(() => {
		return Array.from(stock_subscriptions.keys());
	});

	// Computed getters for stock_subscribed_symbols set
	const getSubscribedSymbolsSet = computed(() => {
		return stock_subscribed_symbols.value;
	});

	const getSubscribedSymbolsArray = computed(() => {
		return Array.from(stock_subscribed_symbols.value);
	});

	const getSubscribedSymbolsCount = computed(() => {
		return stock_subscribed_symbols.value.size;
	});

	// Debounced watcher: wait for symbol changes to settle before fetching quotes.
	// Prevents rapid-fire requests when multiple symbols are added in quick succession.
	let subscribedSymbolsDebounceTimer: ReturnType<typeof setTimeout> | null =
		null;

	watch(getSubscribedSymbolsArray, (newVal, oldVal) => {
		if (!import.meta.client || newVal === oldVal) return;

		// Clear any pending debounce
		if (subscribedSymbolsDebounceTimer) {
			clearTimeout(subscribedSymbolsDebounceTimer);
		}

		subscribedSymbolsDebounceTimer = setTimeout(async () => {
			// Filter out symbols containing special characters, spaces, or empty strings
			const validSymbols = newVal.filter(
				(symbol) =>
					symbol &&
					symbol.trim() !== "" &&
					!/[.+\s]/.test(symbol) &&
					/^[A-Za-z0-9-]+$/.test(symbol)
			);

			if (validSymbols.length === 0) return;

			// ── One-shot REST fetch for immediate badge population ──────────
			try {
				const { data: stock_response, status } = await axios.get(
					`${getSongbirdApiBase()}stocks/quote`,
					{
						params: {
							symbol: validSymbols.join(","),
							include_ytd: "true"
						},
						timeout: 15000,
						validateStatus: () => true
					}
				);

				if (status === 200) {
					addMultipleStockSubscriptions(stock_response.data);
				}
			} catch (error) {
				console.warn(
					"[StocksStore] Failed to fetch subscribed symbols quote:",
					(error as any)?.message || error
				);
			}

			// ── Subscribe these symbols to the WS for live push updates ────
			const newSymbols = validSymbols.filter(
				(s) => !wsSymbols.value.has(s.toUpperCase())
			);
			if (newSymbols.length === 0) return;

			newSymbols.forEach((s) => wsSymbols.value.add(s.toUpperCase()));

			if (ws.value?.readyState === 1 /* OPEN */) {
				// WS already open — send a re-subscribe with the expanded set
				resubscribeWs();
			} else if (!ws.value) {
				// No connection yet — open one now
				connectWs();
			}
			// (if readyState is CONNECTING, the onopen/connected handler will
			// pick up wsSymbols as-is once the socket is ready)
		}, 300); // 300ms debounce
	});

	// Return everything you want to expose from the store
	return {
		// State (exposed as refs)
		stocks,
		symbols_list,
		lastUpdated,
		isLoading,
		error,
		stock_subscribed_symbols,
		usingWs,
		// Getters (exposed as computed refs)
		isDataStale,
		// Actions (exposed as functions)
		init,
		refresh,
		most_active_stocks,
		setMostActiveStocks,
		updateMostActiveStocksCurrentData,
		getMostActiveStocks,
		top_gainers_stocks,
		top_gainers_loaded,
		top_gainers_loading,
		setTopGainerStocks,
		fetchTopGainerStocks,
		primeTopGainerStocksFromBundle,
		getTopGainerStocks,
		top_losers_stocks,
		setTopLoserStocks,
		getTopLoserStocks,
		fetchStocks,
		cleanup,
		fetchStocksData,
		startTrackingSymbols,
		stopTrackingSymbols,
		fetchYtdDataForStocks,
		// Stock subscriptions methods
		addStockSubscription,
		addMultipleStockSubscriptions,
		updateStockSubscription,
		removeStockSubscription,
		clearStockSubscriptions,
		hasStockSubscription,
		getStockSubscription,
		// Stock pairs methods
		setSubscribedSymbolsFromPairs,
		addSubscribedSymbolsFromPairs,
		// Stock subscriptions getters
		getStockSubscriptions,
		getStockSubscriptionsMap,
		getStockSubscriptionsCount,
		getSubscribedSymbols,
		// Stock subscribed symbols getters
		getSubscribedSymbolsSet,
		getSubscribedSymbolsArray,
		getSubscribedSymbolsCount,
		stock_subscriptions
	};
});
